import {
  Alert,
  AlertExplanationContext,
  AlertSection,
  AlertType,
  AlertsCommentary,
  PairedCostRouteItem,
  RouteItem,
  alertRequiresNavAttention,
  getAlertPageKey,
  normalizePairedCostRouteItem,
  resolveRouteInNavOrder,
} from '@aloft/toolkit-shared';

import { isEmpty } from 'lodash';

import { selectAlerts } from '../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import { selectCommentaryV2 } from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import {
  selectIncludeCostApproach,
  selectIsInspectifyOrder,
} from '../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import {
  VALIDATION_STEP_LABELS,
  VALIDATION_STEP_ORDER,
  getRouteForValidationStep,
  routeToStepMap,
} from './consts.ts';
import { ValidationErrorType, ValidationKey, ValidationStep } from './types.ts';
import { useAppraisifyValidation } from './useAppraisifyValidation.ts';

const NAV_ALERT_HANDLED_STEPS = new Set<ValidationStep>([
  'OrderDetails',
  'Analysis',
  'Commentary',
  'Reconciliation',
]);

export const isAlertActiveOnRoute = (alert: Alert, route: RouteItem): boolean => {
  const page = normalizePairedCostRouteItem(route, routeToStepMap);
  if (!page) {
    return false;
  }
  return getAlertPageKey(alert, routeToStepMap) === page;
};

export const isForwardNavBlockingAlert = (alert: Alert): boolean => alert.severity === 'error';

export const getForwardNavBlockingAlertsForRoute = (
  alertsPerSection: Record<AlertSection, Alert[]>,
  route: RouteItem,
  alertsCommentary?: AlertsCommentary,
  explanationContext?: AlertExplanationContext,
): Alert[] =>
  getActiveAlertsForRoute(
    alertsPerSection,
    route,
    alertsCommentary,
    false,
    explanationContext,
  ).filter(isForwardNavBlockingAlert);

export const APPRAISIFY_NAV_ROUTE_ORDER: readonly PairedCostRouteItem[] = [
  'details',
  'features',
  'market',
  'cost-approach',
  'adjustments-grid',
  'appraisify-commentary',
  'rental-features',
  'reconciliation',
];

export const getAppraisifyNavRouteOrder = ({
  includeCostApproach,
}: {
  includeCostApproach: boolean;
}): PairedCostRouteItem[] =>
  APPRAISIFY_NAV_ROUTE_ORDER.filter((route) => route !== 'cost-approach' || includeCostApproach);

export const getCurrentAppraisifyRoute = (pathname: string): PairedCostRouteItem | undefined =>
  [...APPRAISIFY_NAV_ROUTE_ORDER]
    .sort((a, b) => b.length - a.length)
    .find((route) => pathname.includes(`/${route}`));

export const getPreviousAppraisifyRoute = (
  route: RouteItem,
  { includeCostApproach }: { includeCostApproach: boolean },
): PairedCostRouteItem | undefined => {
  const order = getAppraisifyNavRouteOrder({ includeCostApproach });
  const index = order.indexOf(route as PairedCostRouteItem);
  return index > 0 ? order[index - 1] : undefined;
};

export const isForwardAppraisifyNavigation = (
  fromRoute: RouteItem,
  toRoute: RouteItem,
  { includeCostApproach }: { includeCostApproach: boolean },
) => {
  const order = getAppraisifyNavRouteOrder({ includeCostApproach });
  const fromIndex = order.indexOf(fromRoute as PairedCostRouteItem);
  const toIndex = order.indexOf(toRoute as PairedCostRouteItem);
  return fromIndex >= 0 && toIndex > fromIndex;
};

export const getTriggeredAlertsForRoute = (
  triggeredAlertTypes: readonly AlertType[],
  alertsPerSection: Record<AlertSection, Alert[]>,
  route: RouteItem,
): AlertType[] => {
  const page = normalizePairedCostRouteItem(route, routeToStepMap);
  if (!page) {
    return [];
  }

  const alertsByType = new Map(
    Object.values(alertsPerSection)
      .flat()
      .map((alert) => [alert.type, alert]),
  );

  return triggeredAlertTypes.filter((type) => {
    const alert = alertsByType.get(type);
    return alert && isAlertActiveOnRoute(alert, route);
  });
};

export const computeTriggeredAlertsCountBySection = (
  alertsPerSection: Record<AlertSection, Alert[]>,
  triggeredAlertTypes: readonly AlertType[],
  alertsCommentary?: AlertsCommentary,
  explanationContext?: AlertExplanationContext,
): Record<AlertSection, number> =>
  Object.fromEntries(
    Object.entries(alertsPerSection).map(([section, alerts]) => [
      section,
      alerts.filter(
        (alert) =>
          triggeredAlertTypes.includes(alert.type) &&
          alertRequiresNavAttention(alert, alertsCommentary, explanationContext),
      ).length,
    ]),
  ) as Record<AlertSection, number>;

export const getActiveAlertsForRoute = (
  alertsPerSection: Record<AlertSection, Alert[]>,
  route: RouteItem,
  alertsCommentary?: AlertsCommentary,
  errorOnly?: boolean,
  explanationContext?: AlertExplanationContext,
): Alert[] => {
  const page = normalizePairedCostRouteItem(route, routeToStepMap);
  if (!page) {
    return [];
  }

  return Object.values(alertsPerSection)
    .flat()
    .filter(
      (alert) =>
        (!errorOnly || alert.severity === 'error') &&
        alertRequiresNavAttention(alert, alertsCommentary, explanationContext) &&
        isAlertActiveOnRoute(alert, route),
    );
};

export const countActiveAlertsForRoute = (
  alertsPerSection: Record<AlertSection, Alert[]>,
  route: RouteItem,
  alertsCommentary?: AlertsCommentary,
  isRouteAccessible?: (route: RouteItem) => boolean,
  errorOnly?: boolean,
  explanationContext?: AlertExplanationContext,
): number => {
  if (isRouteAccessible && !isRouteAccessible(route)) {
    return 0;
  }
  return getActiveAlertsForRoute(
    alertsPerSection,
    route,
    alertsCommentary,
    errorOnly,
    explanationContext,
  ).length;
};

export const countTriggeredAlertsForRoute = (
  alertsPerSection: Record<AlertSection, Alert[]>,
  route: RouteItem,
  triggeredAlertTypes: readonly AlertType[],
  alertsCommentary?: AlertsCommentary,
  explanationContext?: AlertExplanationContext,
): number =>
  getActiveAlertsForRoute(
    alertsPerSection,
    route,
    alertsCommentary,
    false,
    explanationContext,
  ).filter((alert) => triggeredAlertTypes.includes(alert.type)).length;

export const isRouteValidationStepEnabled = (
  route: RouteItem,
  isStepEnabled: (step: ValidationStep) => boolean,
): boolean => {
  const step = routeToStepMap[route];
  return step ? isStepEnabled(step) : true;
};

export const getEarliestRouteWithActiveAlertsBeforeTarget = (
  alertsPerSection: Record<AlertSection, Alert[]>,
  targetRoute: RouteItem,
  {
    includeCostApproach,
    alertsCommentary,
    explanationContext,
  }: {
    includeCostApproach: boolean;
    alertsCommentary?: AlertsCommentary;
    explanationContext?: AlertExplanationContext;
  },
): PairedCostRouteItem | undefined => {
  const order = getAppraisifyNavRouteOrder({ includeCostApproach });
  const targetIndex = order.indexOf(targetRoute as PairedCostRouteItem);
  if (targetIndex <= 0) {
    return undefined;
  }

  return order
    .slice(0, targetIndex)
    .find(
      (route) =>
        getForwardNavBlockingAlertsForRoute(
          alertsPerSection,
          route,
          alertsCommentary,
          explanationContext,
        ).length > 0,
    );
};

export const getLatestAllowedRouteForValidation = (
  isStepEnabled: (step: ValidationStep) => boolean,
  { includeCostApproach }: { includeCostApproach: boolean },
): PairedCostRouteItem => {
  for (let index = VALIDATION_STEP_ORDER.length - 1; index >= 0; index -= 1) {
    const step = VALIDATION_STEP_ORDER[index];
    if (!step || !isStepEnabled(step)) {
      continue;
    }
    const route = getRouteForValidationStep(step, { includeCostApproach });
    if (route) {
      return route;
    }
  }
  return 'details';
};

export const getForwardNavigationRedirectRoute = (
  currentRoute: PairedCostRouteItem,
  targetRoute: RouteItem,
  alertsPerSection: Record<AlertSection, Alert[]>,
  {
    includeCostApproach,
    alertsCommentary,
    explanationContext,
    isStepEnabled,
    triggeredAlertTypes,
  }: {
    includeCostApproach: boolean;
    alertsCommentary?: AlertsCommentary;
    explanationContext?: AlertExplanationContext;
    isStepEnabled: (step: ValidationStep) => boolean;
    triggeredAlertTypes?: readonly AlertType[];
  },
): PairedCostRouteItem | undefined => {
  const order = getAppraisifyNavRouteOrder({ includeCostApproach });
  const currentIndex = order.indexOf(currentRoute);
  const targetIndex = order.indexOf(targetRoute as PairedCostRouteItem);
  if (currentIndex < 0 || targetIndex <= currentIndex) {
    return undefined;
  }

  const triggered = new Set(triggeredAlertTypes ?? []);
  const currentPageBlockingAlerts = getForwardNavBlockingAlertsForRoute(
    alertsPerSection,
    currentRoute,
    alertsCommentary,
    explanationContext,
  );
  const mustResolveOnCurrentPage = currentPageBlockingAlerts.some(
    (alert) => !triggered.has(alert.type) || alert.fixingInfo.fixingMethod.includes('action'),
  );
  if (mustResolveOnCurrentPage) {
    return currentRoute;
  }

  const latestAllowedRoute = getLatestAllowedRouteForValidation(isStepEnabled, {
    includeCostApproach,
  });
  const latestAllowedIndex = order.indexOf(latestAllowedRoute);

  const targetStep = routeToStepMap[targetRoute];
  if (targetStep && !isStepEnabled(targetStep) && latestAllowedIndex > currentIndex) {
    if (mustResolveOnCurrentPage) {
      return currentRoute;
    }
    return latestAllowedRoute;
  }

  if (targetIndex === currentIndex + 1) {
    if (currentPageBlockingAlerts.length > 0) {
      return currentRoute;
    }
    return undefined;
  }

  const immediateNextRoute = order[currentIndex + 1];
  if (
    immediateNextRoute &&
    getForwardNavBlockingAlertsForRoute(
      alertsPerSection,
      immediateNextRoute,
      alertsCommentary,
      explanationContext,
    ).length > 0
  ) {
    return immediateNextRoute;
  }

  const routeWithAlertsBetween = order
    .slice(currentIndex + 2, targetIndex)
    .find(
      (route) =>
        getForwardNavBlockingAlertsForRoute(
          alertsPerSection,
          route,
          alertsCommentary,
          explanationContext,
        ).length > 0,
    );
  if (routeWithAlertsBetween) {
    return routeWithAlertsBetween;
  }

  if (latestAllowedIndex > currentIndex && targetIndex > latestAllowedIndex) {
    return latestAllowedRoute;
  }

  return undefined;
};

export const getRoutesWithActiveAlertsBetweenNav = (
  currentRoute: PairedCostRouteItem,
  targetRoute: RouteItem,
  alertsPerSection: Record<AlertSection, Alert[]>,
  {
    includeCostApproach,
    alertsCommentary,
    explanationContext,
  }: {
    includeCostApproach: boolean;
    alertsCommentary?: AlertsCommentary;
    explanationContext?: AlertExplanationContext;
  },
): PairedCostRouteItem[] => {
  const order = getAppraisifyNavRouteOrder({ includeCostApproach });
  const currentIndex = order.indexOf(currentRoute);
  const targetIndex = order.indexOf(targetRoute as PairedCostRouteItem);
  if (currentIndex < 0 || targetIndex <= currentIndex) {
    return [];
  }

  return order
    .slice(currentIndex, targetIndex)
    .filter(
      (route) =>
        getForwardNavBlockingAlertsForRoute(
          alertsPerSection,
          route,
          alertsCommentary,
          explanationContext,
        ).length > 0,
    );
};

export const getFurthestForwardNavigableRoute = (
  currentRoute: PairedCostRouteItem,
  alertsPerSection: Record<AlertSection, Alert[]>,
  {
    includeCostApproach,
    alertsCommentary,
    explanationContext,
    isStepEnabled,
    triggeredAlertTypes,
  }: {
    includeCostApproach: boolean;
    alertsCommentary?: AlertsCommentary;
    explanationContext?: AlertExplanationContext;
    isStepEnabled: (step: ValidationStep) => boolean;
    triggeredAlertTypes?: readonly AlertType[];
  },
): PairedCostRouteItem => {
  const order = getAppraisifyNavRouteOrder({ includeCostApproach });
  const currentIndex = order.indexOf(currentRoute);
  if (currentIndex < 0) {
    return currentRoute;
  }

  const latestAllowedRoute = getLatestAllowedRouteForValidation(isStepEnabled, {
    includeCostApproach,
  });
  const latestAllowedIndex = order.indexOf(latestAllowedRoute);

  let furthest = currentRoute;
  for (let targetIndex = currentIndex + 1; targetIndex <= latestAllowedIndex; targetIndex += 1) {
    const targetRoute = order[targetIndex];
    if (!targetRoute) {
      break;
    }
    const redirect = getForwardNavigationRedirectRoute(
      currentRoute,
      targetRoute,
      alertsPerSection,
      {
        includeCostApproach,
        alertsCommentary,
        explanationContext,
        isStepEnabled,
        triggeredAlertTypes,
      },
    );
    if (redirect === undefined) {
      furthest = targetRoute;
      continue;
    }
    break;
  }

  return furthest;
};

export const isRouteWithinAlertNavFrontier = (
  route: RouteItem,
  currentRoute: PairedCostRouteItem,
  alertsPerSection: Record<AlertSection, Alert[]>,
  {
    includeCostApproach,
    alertsCommentary,
    explanationContext,
    isStepEnabled,
    triggeredAlertTypes,
  }: {
    includeCostApproach: boolean;
    alertsCommentary?: AlertsCommentary;
    explanationContext?: AlertExplanationContext;
    isStepEnabled: (step: ValidationStep) => boolean;
    triggeredAlertTypes?: readonly AlertType[];
  },
): boolean => {
  if (!isRouteValidationStepEnabled(route, isStepEnabled)) {
    return false;
  }

  const order = getAppraisifyNavRouteOrder({ includeCostApproach });
  const resolvedRoute = resolveRouteInNavOrder(route as PairedCostRouteItem, order);
  if (!resolvedRoute) {
    return false;
  }
  const routeIndex = order.indexOf(resolvedRoute);

  const frontierRoute = getFurthestForwardNavigableRoute(currentRoute, alertsPerSection, {
    includeCostApproach,
    alertsCommentary,
    explanationContext,
    isStepEnabled,
    triggeredAlertTypes,
  });
  const frontierIndex = order.indexOf(frontierRoute);

  return routeIndex <= frontierIndex;
};

/**
 * This hook is used to determine navigation permissions for the Appraisify wizard steps.
 * It implements sequential validation where steps must be completed in order.
 * If an error exists in an earlier step, all subsequent steps will be disabled.
 * If an error exists in a later step, previous steps remain enabled.
 *
 * @returns Navigation control functions for the appraisify workflow
 * @example
 * const { isStepEnabled } = useAppraisifyNavigation();
 * ...
 * if (isStepEnabled('Analysis')) navigateToStep('Analysis');
 */
export const useAppraisifyNavigation = () => {
  const { errors } = useAppraisifyValidation();
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const dismissedAlerts = useAppSelector(selectAlerts).dismissedAlerts ?? [];
  const finalizedExplanations = useAppSelector((state) => state.ui.finalizedExplanations);
  const explanationContext: AlertExplanationContext = { dismissedAlerts, finalizedExplanations };
  const triggeredAlertTypes = useAppSelector((state) => state.ui.triggeredAlerts);

  const hasStepErrors = (step: ValidationStep) => !isEmpty(errors[step]);

  const hasNavBlockingStepErrors = (step: ValidationStep) =>
    NAV_ALERT_HANDLED_STEPS.has(step) ? false : hasStepErrors(step);

  const getFirstErrorStep = () => VALIDATION_STEP_ORDER.find(hasStepErrors);

  const isStepEnabled = (step: ValidationStep) => {
    const stepIndex = VALIDATION_STEP_ORDER.indexOf(step);

    return !VALIDATION_STEP_ORDER.slice(0, stepIndex).find(hasNavBlockingStepErrors);
  };

  const canCertify = Object.values(errors).every(isEmpty);

  const getLatestAllowedStep = (): ValidationStep => {
    for (let index = VALIDATION_STEP_ORDER.length - 1; index >= 0; index -= 1) {
      const step = VALIDATION_STEP_ORDER[index];
      if (step && isStepEnabled(step)) {
        return step;
      }
    }
    return 'OrderDetails';
  };

  const getLatestAllowedRoute = () =>
    getLatestAllowedRouteForValidation(isStepEnabled, { includeCostApproach });

  const getCertifyErrorText = () => {
    const alertError = Object.values(errors.Alerts ?? {})[0];
    if (alertError) return alertError.message;

    const firstErrorStep = getFirstErrorStep();
    if (firstErrorStep) {
      if (firstErrorStep === 'Global' && errors.Global[ValidationKey.IsEscalated]) {
        return errors.Global[ValidationKey.IsEscalated]?.message;
      }
      if (Object.values(errors.OrderDetails)[0]?.type === ValidationErrorType.INVALID) {
        return `Please correct the errors in the ${VALIDATION_STEP_LABELS.OrderDetails} tab before certifying the report.`;
      }
      return `Please fill out all the required fields in the ${VALIDATION_STEP_LABELS[firstErrorStep]} tab before certifying the report.`;
    }
    return undefined;
  };

  const isRouteAccessibleForOrder = (route: RouteItem) =>
    isRouteValidationStepEnabled(route, isStepEnabled);

  return {
    isStepEnabled,
    isRouteValidationStepEnabled: isRouteAccessibleForOrder,
    hasStepErrors,
    getFirstErrorStep,
    getLatestAllowedStep,
    getLatestAllowedRoute,
    getCertifyErrorText,
    canCertify,
    getCurrentAppraisifyRoute,
    getPreviousAppraisifyRoute: (route: RouteItem) =>
      getPreviousAppraisifyRoute(route, { includeCostApproach }),
    isForwardAppraisifyNavigation: (fromRoute: RouteItem, toRoute: RouteItem) =>
      isForwardAppraisifyNavigation(fromRoute, toRoute, { includeCostApproach }),
    countActiveAlertsForRoute: (
      alertsPerSection: Record<AlertSection, Alert[]>,
      route: RouteItem,
      currentRoute?: PairedCostRouteItem,
    ) => {
      if (
        isInspectifyOrder &&
        currentRoute &&
        !isRouteWithinAlertNavFrontier(route, currentRoute, alertsPerSection, {
          includeCostApproach,
          alertsCommentary,
          explanationContext,
          isStepEnabled,
          triggeredAlertTypes,
        })
      ) {
        return 0;
      }
      return countActiveAlertsForRoute(
        alertsPerSection,
        route,
        alertsCommentary,
        isInspectifyOrder ? isRouteAccessibleForOrder : undefined,
        undefined,
        explanationContext,
      );
    },
    countTriggeredAlertsForRoute: (
      alertsPerSection: Record<AlertSection, Alert[]>,
      route: RouteItem,
    ) =>
      countTriggeredAlertsForRoute(
        alertsPerSection,
        route,
        triggeredAlertTypes,
        alertsCommentary,
        explanationContext,
      ),
    getRoutesWithActiveAlertsBetweenNav: (
      currentRoute: PairedCostRouteItem,
      targetRoute: RouteItem,
      alertsPerSection: Record<AlertSection, Alert[]>,
    ) =>
      getRoutesWithActiveAlertsBetweenNav(currentRoute, targetRoute, alertsPerSection, {
        includeCostApproach,
        alertsCommentary,
        explanationContext,
      }),
    getFurthestForwardNavigableRoute: (
      alertsPerSection: Record<AlertSection, Alert[]>,
      currentRoute: PairedCostRouteItem,
    ) =>
      getFurthestForwardNavigableRoute(currentRoute, alertsPerSection, {
        includeCostApproach,
        alertsCommentary,
        explanationContext,
        isStepEnabled,
        triggeredAlertTypes,
      }),
    isRouteWithinAlertNavFrontier: (
      route: RouteItem,
      currentRoute: PairedCostRouteItem,
      alertsPerSection: Record<AlertSection, Alert[]>,
    ) =>
      isRouteWithinAlertNavFrontier(route, currentRoute, alertsPerSection, {
        includeCostApproach,
        alertsCommentary,
        explanationContext,
        isStepEnabled,
        triggeredAlertTypes,
      }),
    getEarliestRouteWithActiveAlertsBeforeTarget: (
      alertsPerSection: Record<AlertSection, Alert[]>,
      targetRoute: RouteItem,
    ) =>
      getEarliestRouteWithActiveAlertsBeforeTarget(alertsPerSection, targetRoute, {
        includeCostApproach,
        alertsCommentary,
        explanationContext,
      }),
    getForwardNavigationRedirectRoute: (
      currentRoute: PairedCostRouteItem,
      targetRoute: RouteItem,
      alertsPerSection: Record<AlertSection, Alert[]>,
    ) =>
      getForwardNavigationRedirectRoute(currentRoute, targetRoute, alertsPerSection, {
        includeCostApproach,
        alertsCommentary,
        explanationContext,
        isStepEnabled,
        triggeredAlertTypes,
      }),
  };
};
